/*
 * Groups (docs/api.md, "Groups"): people who share access to sites. A group
 * gives every site, listed sites, or every site with a tag, each with a
 * preset or custom levels, and access only adds up: a person can do the most
 * of their own access and each of their groups'. Admins change groups; the
 * API decides who may (owners for owners, billing members never join).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api } from "./client";
import { keys } from "./keys";
import { accessKey, type Abilities, type EnvLevels, type Preset, type SiteGrant } from "./access";
import type { ActivityEvent, Role } from "./types";

export type GroupSummary = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  /** The sites it reaches now (every site, when `everySite`). */
  siteCount: number;
  everySite: boolean;
  tags: string[];
  createdAt: string;
};

export type GroupGrantKind = "all" | "site" | "tag";

export type GroupGrant = {
  kind: GroupGrantKind;
  siteId: string | null;
  siteName: string | null;
  tag: string | null;
  preset: Preset;
  levels: EnvLevels;
  abilities: Abilities;
  /** How many sites it reaches now. */
  sites: number;
  grantedBy: { id: string; name: string } | null;
  grantedAt: string | null;
  extraEnvironments: { id: string; slug?: string; label?: string }[];
};

export type GroupMember = { userId: string; name: string; email: string; role: Role; addedAt: string | null; addedBy: { id: string; name: string } | null };

export type GroupDetail = GroupSummary & { members: GroupMember[]; access: GroupGrant[] };

/** Who loses what if the group goes, or one person leaves it. */
export type GroupImpact = { people: { userId: string; name: string; role: Role; gone: { siteId: string; siteName: string }[]; less: { siteId: string; siteName: string }[] }[] };

const groupsKey = (orgId: string) => [...accessKey(orgId), "groups"] as const;

export function useGroups(enabled = true) {
  const o = useOrg();
  return useQuery({
    queryKey: groupsKey(o.id),
    queryFn: ({ signal }) => api.get<{ groups: GroupSummary[] }>(`/orgs/${o.id}/groups`, signal),
    select: (d) => d.groups,
    enabled,
  });
}

export function useGroup(groupId: string, enabled = true) {
  const o = useOrg();
  return useQuery({
    queryKey: [...groupsKey(o.id), groupId],
    queryFn: ({ signal }) => api.get<{ group: GroupDetail }>(`/orgs/${o.id}/groups/${groupId}`, signal),
    select: (d) => d.group,
    retry: false,
    enabled: enabled && !!groupId,
  });
}

export function useGroupImpact(groupId: string, userId: string | null, enabled: boolean) {
  const o = useOrg();
  return useQuery({
    queryKey: [...groupsKey(o.id), groupId, "impact", userId],
    queryFn: ({ signal }) => api.get<GroupImpact>(`/orgs/${o.id}/groups/${groupId}/impact${userId ? `?userId=${userId}` : ""}`, signal),
    enabled,
    staleTime: 0,
  });
}

/** The group's Activity: what was done to it, in the house sentences. */
export function useGroupActivity(groupId: string) {
  const o = useOrg();
  return useQuery({
    queryKey: [...groupsKey(o.id), groupId, "activity"],
    queryFn: ({ signal }) => api.get<{ events: ActivityEvent[] }>(`/orgs/${o.id}/activity?groupId=${groupId}&limit=50`, signal),
    select: (d) => d.events,
  });
}

/** Any change to groups: every access view (people's, sites', the caller's own) and the members list read again. */
function useGroupWrite<V, R>(fn: (orgId: string, v: V) => Promise<R>) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(o.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accessKey(o.id) });
      void qc.invalidateQueries({ queryKey: keys.members(o.id) });
      void qc.invalidateQueries({ queryKey: ["org", o.id, "activity"] });
      void qc.invalidateQueries({ queryKey: keys.sites(o.id) });
    },
  });
}

export function useCreateGroup() {
  return useGroupWrite((orgId, v: { name: string; description?: string | null }) => api.post<{ group: GroupDetail }>(`/orgs/${orgId}/groups`, v));
}

export function useUpdateGroup(groupId: string) {
  return useGroupWrite((orgId, v: { name?: string; description?: string | null }) => api.patch<{ group: GroupDetail }>(`/orgs/${orgId}/groups/${groupId}`, v));
}

export function useDeleteGroup(groupId: string) {
  return useGroupWrite((orgId, _v: void) => api.del<{ ok: true }>(`/orgs/${orgId}/groups/${groupId}`, {}));
}

export function useAddGroupMembers(groupId: string) {
  return useGroupWrite((orgId, userIds: string[]) => api.post<{ group: GroupDetail }>(`/orgs/${orgId}/groups/${groupId}/members`, { userIds }));
}

export function useRemoveGroupMember(groupId: string) {
  return useGroupWrite((orgId, userId: string) => api.del<{ group: GroupDetail }>(`/orgs/${orgId}/groups/${groupId}/members/${userId}`, {}));
}

/** Where a grant applies, in the API's paths: every site, one site, or a tag. */
export type GrantTarget = { kind: "all" } | { kind: "site"; siteId: string } | { kind: "tag"; tag: string };
const targetPath = (t: GrantTarget) => (t.kind === "all" ? "all" : t.kind === "site" ? `sites/${t.siteId}` : `tags/${encodeURIComponent(t.tag)}`);

export function useSetGroupGrant(groupId: string) {
  return useGroupWrite((orgId, v: { target: GrantTarget; grant: Omit<SiteGrant, "siteId"> }) => api.put<{ group: GroupDetail }>(`/orgs/${orgId}/groups/${groupId}/access/${targetPath(v.target)}`, v.grant));
}

export function useRemoveGroupGrant(groupId: string) {
  return useGroupWrite((orgId, target: GrantTarget) => api.del<{ group: GroupDetail }>(`/orgs/${orgId}/groups/${groupId}/access/${targetPath(target)}`, {}));
}
